import { useState } from 'react';
import { Upload, X, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, FileSpreadsheet, Loader as Loader2 } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { BankAccount, AccountingCategory } from '../../types/database';
import { knownActionsService, KnownActionInsert } from '../../lib/knownActionsService';

// ── Date sanitization (same logic as ImportTransactionsModal) ─────────────────

function sanitizeDate(raw: unknown): string {
  if (typeof raw === 'number') {
    if (raw >= 1 && raw <= 2958465) {
      const parsed = XLSX.SSF.parse_date_code(raw);
      if (parsed && parsed.y && parsed.m && parsed.d) {
        return `${String(parsed.d).padStart(2, '0')}/${String(parsed.m).padStart(2, '0')}/${parsed.y}`;
      }
    }
    return String(raw);
  }
  const s = String(raw ?? '').trim();
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  return s;
}

function sanitizeAmount(raw: unknown): string | number {
  if (typeof raw === 'number') return raw;
  return String(raw ?? '').replace(/(\d),(\d)/g, '$1$2');
}

function parseDate(dateStr: string): string | null {
  const parts = dateStr.trim().split('/');
  if (parts.length !== 3) return null;
  const day   = parseInt(parts[0] ?? '', 10);
  const month = parseInt(parts[1] ?? '', 10);
  const year  = parseInt(parts[2] ?? '', 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseAmount(raw: string | number): number | null {
  if (typeof raw === 'number') return raw;
  const cleaned = raw.replace(/[₪$€,\s]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

// Expected columns: תאריך | תיאור | סכום | חשבון בנק (optional) | קטגוריה (optional)
const REQUIRED_HEADERS = ['תאריך', 'תיאור', 'סכום'];

function validateHeaders(headers: string[]): boolean {
  if (headers.length < 3) return false;
  for (let i = 0; i < 3; i++) {
    if ((headers[i] ?? '').trim() !== REQUIRED_HEADERS[i]) return false;
  }
  return true;
}

interface ParsedRow {
  תאריך: string;
  תיאור: string;
  סכום: string | number;
  'חשבון בנק'?: string;
  קטגוריה?: string;
}

function parseRows(raw: unknown[][]): ParsedRow[] {
  const rows: ParsedRow[] = [];
  for (let i = 1; i < raw.length; i++) {
    const row = raw[i] as unknown[];
    if (!row || row.length < 3) continue;
    rows.push({
      תאריך:       sanitizeDate(row[0]),
      תיאור:       row[1] != null ? String(row[1]).trim() : '',
      סכום:        sanitizeAmount(row[2]),
      'חשבון בנק': row[3] != null ? String(row[3]).trim() : '',
      קטגוריה:     row[4] != null ? String(row[4]).trim() : '',
    });
  }
  return rows;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ImportKnownActionsModalProps {
  isOpen:               boolean;
  onClose:              () => void;
  onSuccess:            () => void;
  bankAccounts:         BankAccount[];
  accountingCategories: AccountingCategory[];
  companyId:            string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ImportKnownActionsModal({
  isOpen,
  onClose,
  onSuccess,
  bankAccounts,
  accountingCategories,
  companyId,
}: ImportKnownActionsModalProps) {
  const [defaultAccountId,   setDefaultAccountId]   = useState('');
  const [defaultCategoryId,  setDefaultCategoryId]  = useState('');
  const [file,               setFile]               = useState<File | null>(null);
  const [dragging,           setDragging]           = useState(false);
  const [uploading,          setUploading]          = useState(false);
  const [error,              setError]              = useState<string | null>(null);
  const [validationErrors,   setValidationErrors]   = useState<string[]>([]);

  if (!isOpen) return null;

  const reset = () => {
    setDefaultAccountId('');
    setDefaultCategoryId('');
    setFile(null);
    setError(null);
    setValidationErrors([]);
  };

  const handleClose = () => { reset(); onClose(); };

  const validateFile = (f: File) => {
    const ext = f.name.toLowerCase().split('.').pop();
    if (ext !== 'csv' && ext !== 'xlsx') {
      setError('קובץ לא תקין. יש להעלות קובץ CSV או XLSX בלבד.');
      setFile(null);
      return;
    }
    setFile(f);
    setError(null);
    setValidationErrors([]);
  };

  const readFile = (f: File): Promise<ParsedRow[]> =>
    new Promise((resolve, reject) => {
      const ext = f.name.toLowerCase().split('.').pop();

      if (ext === 'csv') {
        Papa.parse<Record<string, unknown>>(f, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h: string) => h.trim(),
          complete: (results) => {
            const headers = (results.meta.fields ?? []).map(h => h.trim());
            if (!validateHeaders(headers)) {
              reject(new Error('כותרות לא תקינות. ודא שהעמודות הראשונות הן: תאריך | תיאור | סכום'));
              return;
            }
            const rows = (results.data as Record<string, unknown>[]).map(r => ({
              תאריך:       sanitizeDate(r['תאריך']),
              תיאור:       String(r['תיאור'] ?? '').trim(),
              סכום:        sanitizeAmount(r['סכום']),
              'חשבון בנק': String(r['חשבון בנק'] ?? '').trim(),
              קטגוריה:     String(r['קטגוריה'] ?? '').trim(),
            }));
            resolve(rows);
          },
          error: (err: Error) => reject(err),
        });
        return;
      }

      // XLSX
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0] ?? ''];
          if (!ws) { reject(new Error('קובץ ריק')); return; }
          const raw = XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown[][];
          if (raw.length < 2) { reject(new Error('קובץ ריק')); return; }
          const headers = (raw[0] as unknown[]).map(h => (h != null ? String(h).trim() : ''));
          if (!validateHeaders(headers)) {
            reject(new Error('כותרות לא תקינות. ודא שהעמודות הראשונות הן: תאריך | תיאור | סכום'));
            return;
          }
          resolve(parseRows(raw));
        } catch (err) { reject(err); }
      };
      reader.onerror = () => reject(new Error('שגיאה בקריאת הקובץ'));
      reader.readAsArrayBuffer(f);
    });

  const handleImport = async () => {
    if (!file || !defaultAccountId) {
      setError('יש לבחור חשבון בנק ולהעלות קובץ.');
      return;
    }
    setUploading(true);
    setError(null);
    setValidationErrors([]);

    try {
      const parsed = await readFile(file);

      // Build lookup maps for name → id matching from the spreadsheet columns
      const bankByNick = new Map(bankAccounts.map(b => [b.nickname.trim().toLowerCase(), b.id]));
      const catByName  = new Map(accountingCategories.map(c => [c.name.trim().toLowerCase(), c.id]));

      const toInsert: KnownActionInsert[] = [];
      const errors: string[] = [];

      parsed.forEach((row, idx) => {
        const rowNum = idx + 2;

        const date = parseDate(row.תאריך);
        if (!date) { errors.push(`שורה ${rowNum}: תאריך לא תקין (${row.תאריך})`); return; }

        const description = row.תיאור;
        if (!description) { errors.push(`שורה ${rowNum}: תיאור חסר`); return; }

        const amount = parseAmount(row.סכום);
        if (amount === null) { errors.push(`שורה ${rowNum}: סכום לא תקין (${row.סכום})`); return; }

        // Resolve bank account: if column is provided it MUST match a known account
        const bankNick = (row['חשבון בנק'] ?? '').trim();
        let bankId: string;
        if (bankNick) {
          const matched = bankByNick.get(bankNick.toLowerCase());
          if (!matched) {
            const validNames = bankAccounts.map(b => `"${b.nickname}"`).join(', ');
            errors.push(`שורה ${rowNum}: חשבון בנק "${bankNick}" לא קיים במערכת. אפשרויות תקינות: ${validNames}`);
            return;
          }
          bankId = matched;
        } else {
          bankId = defaultAccountId;
        }

        // Resolve category: if column is provided it MUST match a known category
        const catName = (row.קטגוריה ?? '').trim();
        let catId: string;
        if (catName) {
          const matched = catByName.get(catName.toLowerCase());
          if (!matched) {
            const validNames = accountingCategories.map(c => `"${c.name}"`).join(', ');
            errors.push(`שורה ${rowNum}: קטגוריה "${catName}" לא קיימת במערכת. אפשרויות תקינות: ${validNames}`);
            return;
          }
          catId = matched;
        } else {
          catId = defaultCategoryId;
        }

        if (!catId) { errors.push(`שורה ${rowNum}: קטגוריה חסרה — יש לבחור קטגוריה ברירת מחדל`); return; }

        toInsert.push({
          company_id:             companyId,
          action_date:            date,
          description,
          amount,
          bank_account_id:        bankId,
          accounting_category_id: catId,
        });
      });

      if (errors.length > 0) {
        setValidationErrors(errors.slice(0, 10));
        if (toInsert.length === 0) { setUploading(false); return; }
      }

      if (toInsert.length > 0) {
        const result = await knownActionsService.bulkCreateKnownActions(toInsert);
        if (result.error) throw new Error(String((result.error as { message?: string }).message ?? result.error));
        handleClose();
        onSuccess();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'שגיאה בייבוא');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">ייבוא פעולות ידועות מאקסל</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors" disabled={uploading}>
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Default account */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              חשבון בנק ברירת מחדל <span className="text-red-500">*</span>
            </label>
            <select
              value={defaultAccountId}
              onChange={e => setDefaultAccountId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={uploading}
            >
              <option value="">בחר חשבון...</option>
              {bankAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.nickname} — {a.bank_name} ({a.account_number})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">ישמש לשורות ללא עמודת "חשבון בנק" בקובץ</p>
          </div>

          {/* Default category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              קטגוריה ברירת מחדל <span className="text-gray-400 text-xs font-normal">(אופציונלי — לשורות ללא עמודת קטגוריה)</span>
            </label>
            <select
              value={defaultCategoryId}
              onChange={e => setDefaultCategoryId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={uploading}
            >
              <option value="">בחר קטגוריה...</option>
              {accountingCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">ישמש לשורות ללא עמודת "קטגוריה" בקובץ</p>
          </div>

          {/* Format instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              פורמט הקובץ הנדרש
            </h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p>שמות עמודות חובה (בדיוק):</p>
              <ul className="list-disc list-inside mr-4 space-y-0.5">
                <li><strong>תאריך</strong> — DD/MM/YYYY או YYYY-MM-DD</li>
                <li><strong>תיאור</strong> — טקסט חופשי</li>
                <li><strong>סכום</strong> — מספר (חיובי = הכנסה, שלילי = הוצאה)</li>
              </ul>
              <p className="mt-2">עמודות אופציונליות:</p>
              <ul className="list-disc list-inside mr-4 space-y-0.5">
                <li><strong>חשבון בנק</strong> — שם הכינוי של החשבון</li>
                <li><strong>קטגוריה</strong> — שם הקטגוריה</li>
              </ul>
            </div>
          </div>

          {/* Dropzone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) validateFile(f); }}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragging ? 'border-blue-500 bg-blue-50' : file ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
            }`}
          >
            <input type="file" accept=".csv,.xlsx" id="known-file-upload" className="hidden" disabled={uploading}
              onChange={e => { const f = e.target.files?.[0]; if (f) validateFile(f); }} />
            {file ? (
              <div className="space-y-3">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />
                <div>
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
                <button onClick={() => setFile(null)} className="text-sm text-blue-600 hover:text-blue-700 underline" disabled={uploading}>
                  החלף קובץ
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="w-12 h-12 mx-auto text-gray-400" />
                <div>
                  <label htmlFor="known-file-upload" className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium">
                    לחץ להעלאת קובץ
                  </label>
                  <p className="text-sm text-gray-500 mt-1">או גרור ושחרר לכאן</p>
                </div>
                <p className="text-xs text-gray-500">CSV או XLSX</p>
              </div>
            )}
          </div>

          {/* Validation errors */}
          {validationErrors.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900 mb-2">שגיאות אימות</h4>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    {validationErrors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                  {validationErrors.length >= 10 && (
                    <p className="text-xs text-yellow-700 mt-2">מוצגות 10 שגיאות ראשונות בלבד</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* General error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button onClick={handleClose} className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium" disabled={uploading}>
            ביטול
          </button>
          <button
            onClick={handleImport}
            disabled={!file || !defaultAccountId || uploading}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> מייבא...</>
            ) : (
              <><Upload className="w-4 h-4" /> ייבא פעולות</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
